import type { BookSearchResult } from '@/types/generated'
import { book_path } from '@/routes'

export interface SearchHit {
  objectID: string
  record_type: 'book' | 'author' | 'series' | 'user' | 'club'
  record_id: number
  title: string
  subtitle?: string
  description?: string
  image_url?: string
  url: string
}

export type ExternalBook = BookSearchResult

export type SearchResultItem =
  { kind: 'algolia'; hit: SearchHit } | { kind: 'external'; book: ExternalBook }

export function takeTopAlgoliaHits(hits: SearchHit[], limit = 10): SearchHit[] {
  return hits.slice(0, limit)
}

export function searchFinishedWithNoResults({
  query,
  resultCount,
  isSearching,
  isSearchingBooks,
  isSearchPending,
}: {
  query: string
  resultCount: number
  isSearching: boolean
  isSearchingBooks: boolean
  isSearchPending: boolean
}): boolean {
  return (
    query.length >= 2 &&
    resultCount === 0 &&
    !isSearching &&
    !isSearchingBooks &&
    !isSearchPending
  )
}

/** Compose result types for display without changing the server's book order. */
export function mergeSearchResults(
  otherHits: SearchHit[],
  books: BookSearchResult[]
): SearchResultItem[] {
  const bookItems: SearchResultItem[] = books.map((book) =>
    book.in_database && book.id != null
      ? {
          kind: 'algolia',
          hit: {
            objectID: `book_${book.id}`,
            record_type: 'book',
            record_id: book.id,
            title: book.title,
            subtitle: book.author ?? undefined,
            image_url: book.cover_url ?? undefined,
            url: book_path(book.id),
          },
        }
      : { kind: 'external', book }
  )
  const items: SearchResultItem[] = otherHits.map((hit) => ({
    kind: 'algolia',
    hit,
  }))
  const firstNonUser = otherHits.findIndex((hit) => hit.record_type !== 'user')
  items.splice(
    firstNonUser === -1 ? items.length : firstNonUser,
    0,
    ...bookItems
  )
  return items
}
